import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import SearchStore from './search.store';
import chatConnection from '../services/connection/chat.connection';
import ILogChat, { Role, ItemType, ParamsObject } from '../models/logchat.model';
import ILog from '../models/log.model';

import commonStore from './common.store';
import Popup from '../components/common/popup.component';
import moment from 'moment';
import { getLogger } from '../services/logger.service';

const log = getLogger('LogChatStore');

export interface IListItem {
  type: 'chat' | 'date';
  content: Date | ILogChat;
}
export class LogChatStore {
  logChat?: ILogChat = undefined;
  loading: boolean = false;
  chatList: ILogChat[] = [];
  scrollToIndex: number = -1;
  inputPrompt: string = '';
  prompt: string = '';

  constructor() {
    makeObservable(this, {
      logChat: observable,
      loading: observable,
      chatList: observable,
      scrollToIndex: observable,
      inputPrompt: observable,
      prompt: observable,
      setPrompt: action,
      setScrollToIndex: action,
      clearChat: action,
      startLogChat: action,
      chat: action,
      getChat: action,
      getChatList: action,
      deleteChat: action,
      updateChatName: action,
      messages: computed,
      searchArray: computed,
      list: computed
    });
  }

  setPrompt =(prompt: string) => {
    this.prompt = prompt;
  }

  setScrollToIndex(index: number) {
    log.d('setScrollToIndex: ' + index + ' original: ' + this.scrollToIndex);
    this.scrollToIndex = index;
  }

  get messages() {
    // empty chat
    if (!this.logChat?.items) {
      if (this.inputPrompt.length > 0)
        return [{msg: {role: Role.User, content: this.inputPrompt}, searchIndex: -1}];
      return [];
    }

    // chat with items
    let searchIndex = -1;
    const msgs: {msg: {role: Role; content: string}; searchIndex: number}[] = [];
    for (let i = 0; i < this.logChat.items.length; i++) {
      const item = this.logChat.items[i];
      
      // Check if the next item is a function_call (which means logs will be retrieved)
      const nextItem = this.logChat.items[i + 1];
      if (nextItem?.type === ItemType.FunctionCall) searchIndex++;

      // Only show message items (not function_call or function_call_output)
      if (item.type === ItemType.Message && item.role !== Role.System) {
        msgs.push({msg: {role: item.role, content: item.content}, searchIndex});
      }
    }

    return msgs;
  }

  get searchArray() {
    const items = this.logChat?.items;
    if (!items) return [];
    
    const searchArray: {title: string; search: {query: ParamsObject; logs: ILog[]}; index: number}[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type === ItemType.FunctionCallOutput && item.search) {
        // Find the preceding user message for title
        let title = '';
        for (let j = i - 1; j >= 0; j--) {
          const prevItem = items[j];
          if (prevItem.type === ItemType.Message && prevItem.role === Role.User) {
            title = prevItem.content;
            break;
          }
        }
        searchArray.push({title, search: item.search, index: i});
      }
    }
    return searchArray;
  }

  get list() {
    const list: IListItem[] = [];
    let currentDate:Date;
    this.chatList.forEach(chat => {
      if (!currentDate || !moment(chat.createdAt).isSame(currentDate, 'day')) {
        currentDate = chat.createdAt;
        list.push({ type: 'date', content: chat.createdAt });
      }
      list.push({ type: 'chat', content: chat});
    });
    return list;
  }

  clearChat() { 
    this.logChat = undefined;
    this.inputPrompt = '';
  }

  async startLogChat() {
    this.loading = true;
    commonStore.setLoading(true);
    this.logChat = undefined;
    this.inputPrompt = '';
    try {
      const data = SearchStore.logsParams;
      const res = await chatConnection.startLogChat(data);
      runInAction(() => {
        this.logChat = res.chat;
      });
    }
    catch (error) {
      log.e('error starting chat', error);
      Popup.createError({msg: 'Had a problem starting chat, please try later again'}); 
    }
    finally {
      runInAction(() => {
        commonStore.setLoading(false);
        this.loading = false;
      });
    }
  }

  async chat(prompt: string) {
    const logChat = this.logChat;
    this.loading = true;

    try {
      let res: {chat: ILogChat};
      if (logChat) {
        // Add user message to items locally before sending
        logChat.items.push({
          type: ItemType.Message,
          role: Role.User,
          content: prompt
        });
        res = await chatConnection.chat(logChat._id, prompt);
      }
      else { // new chat
        this.inputPrompt = prompt;
        res = await chatConnection.start(prompt);
      }
  
      runInAction(() => {
        this.logChat = res.chat;
      });
    }
    catch (error) {
      log.e('error posting message', error);
      Popup.createError({msg: 'Had a problem posting message, please try later again.'});
    }
    finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async getChat(chatId: string) {
    this.loading = true;
    try {
      const res = await chatConnection.getChat(chatId);
      runInAction(() => {
        this.logChat = res.chat;
      });
    }
    catch (error) {
      log.e('error loading chat', error);
      Popup.createError({msg: 'Had a problem loading chat, please try later again'}); 
    }
    finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async getChatList() {
    this.loading = true;
    try {
      const res = await chatConnection.getChatList();
      runInAction(() => {
        this.chatList = res;
      });
    }
    catch (error) {
      log.e('error loading chat list', error);
      Popup.createError({msg: 'Had a problem loading chat list, please try later again'}); 
    }
    finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async deleteChat(chatId: string) {
    this.loading = true;
    try {
      await chatConnection.deleteChat(chatId);
      runInAction(() => {
        this.chatList = this.chatList.filter(chat => chat._id !== chatId);
      });
    }
    catch (error) {
      log.e('error deleting chat', error);
      Popup.createError({msg: 'Had a problem deleting chat, please try later again'}); 
    }
    finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async updateChatName(name: string) {
    this.loading = true;
    try {
      const res  = await chatConnection.updateChat(this.logChat!._id, {name});
      runInAction(() => {
        this.logChat = res.chat;
        this.chatList = this.chatList.map(chat => {
          if (chat._id === this.logChat!._id) {
            chat.name = name;
          }
          return chat;
        });
      });
    }
    catch (error) {
      log.e('error updating chat name', error);
      Popup.createError({msg: 'Had a problem updating chat name, please try later again'}); 
    }
    finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
}

export default new LogChatStore();
